import React, { useEffect, useMemo, useRef } from "react";
import { ClockCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Form, Input, InputNumber, Space, Switch, Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";

import { estimateWorkoutSeconds } from "@fitness-app/client-shared/features/live-workout/calc-workout-time";
import { DEFAULT_REST_BETWEEN_EXERCISES, type ProgramWorkout } from "@fitness-app/data-models/entities/TrainingProgram";

import { useWorkoutActionProviderContext } from "~/modules/TrainingPrograms/ProgramBuilder/WorkoutDay/providers/WorkoutActionProvider";

export interface WorkoutDayNameProps {
  selectedWorkout: ProgramWorkout;
  programId: string;
}

export interface WorkoutMetadataForm {
  name: string;
  description: string;
  isRestDay: boolean;
  restBetweenExercises: number | null;
}

const WorkoutDayMetadata = ({ selectedWorkout }: WorkoutDayNameProps) => {
  const { t } = useTranslation(["workouts", "common"]);
  const [formController] = Form.useForm<WorkoutMetadataForm>();
  const { updateWorkoutMetadata } = useWorkoutActionProviderContext();
  const name = Form.useWatch("name", formController);
  const description = Form.useWatch("description", formController);
  const isRestDay = Form.useWatch("isRestDay", formController);
  const restBetweenExercises = Form.useWatch("restBetweenExercises", formController);
  const [debouncedRest] = useDebounce(restBetweenExercises, 500);
  const [debouncedName] = useDebounce(name, 500) || "";
  const [debouncedDescValue] = useDebounce(description, 500) || "";

  const originalRestDay = selectedWorkout.isRestDay;

  const updateRef = useRef(updateWorkoutMetadata);

  useEffect(() => {
    updateRef.current = updateWorkoutMetadata;
  }, [updateWorkoutMetadata]);

  useEffect(() => {
    if (typeof isRestDay === "boolean" && isRestDay !== originalRestDay) {
      void updateRef.current({ isRestDay });
    }
  }, [originalRestDay, isRestDay]);

  const savedRest = selectedWorkout.restBetweenExercises ?? null;

  useEffect(() => {
    // undefined = the form has not mounted the field yet; null = the trainer cleared it (back to the default).
    if (debouncedRest !== undefined && debouncedRest !== savedRest) {
      void updateRef.current({ restBetweenExercises: debouncedRest });
    }
  }, [debouncedRest, savedRest]);

  const hasExercises =
    selectedWorkout.preWorkout.length + selectedWorkout.exercises.length + selectedWorkout.postWorkout.length > 0;
  const estimatedMinutes = useMemo(
    () => Math.max(1, Math.round(estimateWorkoutSeconds(selectedWorkout) / 60)),
    [selectedWorkout],
  );

  const selectedWorkoutName = selectedWorkout.name;
  const selectedWorkoutDescription = selectedWorkout.description;

  useEffect(() => {
    const trimmedValue = debouncedName?.trim();
    if (trimmedValue && trimmedValue !== selectedWorkoutName) {
      void updateRef.current({ name: trimmedValue });
    }
  }, [debouncedName, selectedWorkoutName, t]);

  useEffect(() => {
    if (typeof debouncedDescValue === "string" && debouncedDescValue != selectedWorkoutDescription) {
      void updateRef.current({ description: debouncedDescValue });
    }
  }, [debouncedDescValue, selectedWorkoutDescription]);

  return (
    <Space direction="vertical" style={{ width: "100%", margin: "20px 0" }}>
      <Form<WorkoutMetadataForm>
        layout="inline"
        form={formController}
        className="gap-y-3"
        initialValues={{
          name: selectedWorkout.name,
          description: selectedWorkout.description,
          isRestDay: selectedWorkout.isRestDay ?? false,
          restBetweenExercises: savedRest,
        }}
      >
        <Form.Item
          label={t("workoutMetadata.title")}
          name="name"
          rules={[
            {
              min: 1,
              max: 300,
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={t("workoutMetadata.description")}
          name="description"
          rules={[
            {
              min: 0,
              max: 500,
              required: false,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Input.TextArea style={{ width: 300 }} rows={1} />
        </Form.Item>

        <Form.Item label={t("workoutMetadata.isRestDay")} name="isRestDay" valuePropName="checked">
          <Switch />
        </Form.Item>

        {!selectedWorkout.isRestDay && (
          <Form.Item
            label={
              <Tooltip
                title={t("workoutMetadata.restBetweenExercisesTooltip", { seconds: DEFAULT_REST_BETWEEN_EXERCISES })}
              >
                <span className="flex items-center gap-1">
                  {t("workoutMetadata.restBetweenExercises")}
                  <InfoCircleOutlined className="text-gray-400" />
                </span>
              </Tooltip>
            }
            name="restBetweenExercises"
          >
            <InputNumber
              min={0}
              max={1800}
              precision={0}
              placeholder={String(DEFAULT_REST_BETWEEN_EXERCISES)}
              addonAfter={t("duration.secondsAbv")}
              style={{ width: 130 }}
            />
          </Form.Item>
        )}

        {!selectedWorkout.isRestDay && hasExercises && (
          <Form.Item>
            <Tag icon={<ClockCircleOutlined />} className="m-0">
              {t("workoutMetadata.estimatedTime", { time: `~${estimatedMinutes} min` })}
            </Tag>
          </Form.Item>
        )}
      </Form>
    </Space>
  );
};

export default WorkoutDayMetadata;
